
import React, { useEffect } from 'react';
import { useLanguage } from './LanguageContext';
import FadeIn from './FadeIn';
import SEO from './SEO';

const AccessibilityStatement: React.FC = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <section className="min-h-screen bg-[#120C08] pt-32 pb-24 relative">
      <SEO 
        title={language === 'he' ? 'הצהרת נגישות' : 'Accessibility Statement'} 
        description="הצהרת נגישות לאתר יהודה פייט"
        path="/accessibility"
      />
      
      <div className="max-w-4xl mx-auto px-6 relative z-10 text-white font-sans">
        <FadeIn>
          <h1 className="text-4xl font-serif font-bold text-fait-gold mb-8">
            {language === 'he' ? 'הצהרת נגישות' : 'Accessibility Statement'}
          </h1>
          
          <div className="space-y-6 text-gray-300 leading-relaxed bg-[#1a120b] p-8 rounded-2xl border border-white/5">
            {language === 'he' ? (
              <>
                <p>
                  אנו ב"יהודה פייט" רואים חשיבות עליונה במתן שירות שוויוני לכלל הלקוחות ובשיפור השירות הניתן ללקוחות עם מוגבלות. אנו משקיעים משאבים רבים בהנגשת האתר והמקום הפיזי על מנת לאפשר לכל אדם לגלוש בביטחון ובנוחות.
                </p>

                <h2 className="text-2xl font-bold text-white mt-6">נגישות אתר האינטרנט</h2>
                <p>
                  אתר זה עומד בדרישות תקנות שוויון זכויות לאנשים עם מוגבלות (התאמות נגישות לשירות), התשע"ג 2013.
                  התאמות הנגישות בוצעו ע"פ המלצות התקן הישראלי (ת"י 5568) לנגישות תכנים באינטרנט ברמת AA ומסמך WCAG2.0 הבינלאומי.
                </p>
                <ul className="list-disc list-inside space-y-2 mr-4">
                    <li>האתר מותאם לתצוגה בדפדפנים הנפוצים ולשימוש בטלפון הסלולרי.</li>
                    <li>האתר מספק מבנה סמנטי עבור טכנולוגיות מסייעות ותמיכה בדפוס השימוש המקובל להפעלה עם מקלדת בעזרת מקשי החיצים, Enter ו- Esc ליציאה מתפריטים וחלונות.</li>
                    <li>לשם קבלת חווית גלישה מיטבית עם תוכנת הקראת מסך, אנו ממליצים להשתמש בתוכנת NVDA העדכנית ביותר.</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-6">הסדרי נגישות פיזיים</h2>
                <p>
                  <strong>כתובת:</strong> רחוב הירקון 39, בני ברק.
                </p>
                <ul className="list-disc list-inside space-y-2 mr-4">
                    <li>המקום ממוקם בקומה 2.</li>
                    <li>ישנה מעלית נגישה בבניין.</li>
                    <li>הכניסה והמעברים במקום מרווחים ומותאמים לכיסא גלגלים.</li>
                    <li>ניתן להיכנס עם כלב נחייה.</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-6">דרכי פנייה לבקשות והצעות שיפור בנושא נגישות</h2>
                <p>
                  אם נתקלתם בבעיה או בתקלה כלשהי בנושא הנגישות, נשמח שתעדכנו אותנו בכדי שנוכל למצוא פתרון מתאים ולטפל בתקלה בהקדם ככל שניתן.
                </p>
                <p>
                  <strong>פרטי רכז הנגישות / איש קשר:</strong><br/>
                  שם: יהודה פייט<br/>
                  טלפון: <a href="tel:0548478033" className="text-fait-gold hover:underline">054-847-8033</a><br/>
                </p>
              </>
            ) : (
              <>
                <p>
                  At "Yehuda Fait", we consider it of utmost importance to provide equal service to all customers and to improve the service provided to customers with disabilities. We invest significant resources in making our website and physical location accessible to allow everyone to browse with confidence and comfort.
                </p>

                <h2 className="text-2xl font-bold text-white mt-6">Website Accessibility</h2>
                <p>
                  This website meets the requirements of the Equal Rights for Persons with Disabilities Regulations (Service Accessibility Adjustments), 2013.
                  Accessibility adjustments were made according to the recommendations of the Israeli Standard (IS 5568) for web content accessibility at AA level and the international WCAG2.0 document.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>The site is adapted for display in common browsers and for use on mobile phones.</li>
                    <li>The site provides semantic structure for assistive technologies and supports standard keyboard operation patterns using arrow keys, Enter, and Esc to exit menus and windows.</li>
                    <li>For the best browsing experience with screen reading software, we recommend using the latest NVDA software.</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-6">Physical Accessibility Arrangements</h2>
                <p>
                  <strong>Address:</strong> 39 HaYarkon St, Bnei Brak.
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Located on the 2nd floor.</li>
                    <li>There is an accessible elevator in the building.</li>
                    <li>Entrance and aisles are spacious and wheelchair accessible.</li>
                    <li>Service animals are welcome.</li>
                </ul>

                <h2 className="text-2xl font-bold text-white mt-6">Contact for Accessibility Requests</h2>
                <p>
                  If you encounter any problem or malfunction regarding accessibility, we would appreciate it if you let us know so we can find a suitable solution and address the malfunction as soon as possible.
                </p>
                <p>
                  <strong>Contact Person:</strong><br/>
                  Name: Yehuda Fait<br/>
                  Phone: <a href="tel:0548478033" className="text-fait-gold hover:underline">054-847-8033</a><br/>
                </p>
              </>
            )}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default AccessibilityStatement;
